// FLAGS: none
// The scheduler's translation surface: source language, plus one editable panel per added language.
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import FormField from "@src/components/spaces/FormField";
import { Select, fieldClass } from "@src/components/spaces/ui";
import {
  TranslationDraft,
  emptyDraft,
  filledCount,
} from "@src/components/spaces/translationDrafts";
import {
  CONTENT_LANGUAGES,
  CONTENT_LANGUAGE_LABELS,
  ContentLanguage,
} from "@src/models/spaces";

/**
 * One session as this panel needs to see it: something to key its draft by, a
 * heading, and the source copy to show beside the translation.
 *
 * Deliberately not a SpaceSession. On the create form the sessions do not exist
 * yet and have no ids, so the panel is given this shape by both callers rather
 * than reading a stored event — which is what lets one component serve both
 * (OI-25388).
 */
export interface TranslatableSession {
  /** Session id when editing; the client-side draft key when creating. */
  key: string;
  /** Heading shown above the session's rows, e.g. "Session 1". */
  label: string;
  sourceName: string;
  sourceInstructions: string;
}

interface EventTranslationsProps {
  /** The language the source copy below is written in; "" until one is picked. */
  sourceLanguage: ContentLanguage | "";
  /** The event's own copy, shown read-only beside each translation. */
  title: string;
  description: string;
  sessions: readonly TranslatableSession[];
  translations: TranslationDraft[];
  languageError?: string;
  onSourceLanguageChange: (next: ContentLanguage | "") => void;
  onTranslationsChange: (next: TranslationDraft[]) => void;
}

/**
 * Translations are edited beside the copy they translate, not behind a tab that
 * hides it: a translator needs the source in view, and flipping back and forth
 * to check a phrase is where mistakes come from. The source column is read-only
 * here — it is edited above, in the event's own fields.
 */
const EventTranslations = ({
  sourceLanguage,
  title,
  description,
  sessions,
  translations,
  languageError,
  onSourceLanguageChange,
  onTranslationsChange,
}: EventTranslationsProps) => {
  const [openLanguage, setOpenLanguage] = useState<ContentLanguage | null>(
    translations[0]?.languageCode ?? null,
  );

  const used = new Set(translations.map((draft) => draft.languageCode));
  const addable = CONTENT_LANGUAGES.filter(
    (language) => language !== sourceLanguage && !used.has(language),
  );

  const updateDraft = (
    languageCode: ContentLanguage,
    change: Partial<TranslationDraft>,
  ) =>
    onTranslationsChange(
      translations.map((draft) =>
        draft.languageCode === languageCode ? { ...draft, ...change } : draft,
      ),
    );

  const updateSession = (
    languageCode: ContentLanguage,
    sessionKey: string,
    change: Partial<{ name: string; sessionInstructions: string }>,
  ) => {
    const draft = translations.find(
      (entry) => entry.languageCode === languageCode,
    );
    if (!draft) return;
    updateDraft(languageCode, {
      sessions: {
        ...draft.sessions,
        [sessionKey]: { ...draft.sessions[sessionKey], ...change },
      },
    });
  };

  const addLanguage = (languageCode: ContentLanguage) => {
    onTranslationsChange([
      ...translations,
      emptyDraft(
        languageCode,
        sessions.map((session) => session.key),
      ),
    ]);
    setOpenLanguage(languageCode);
  };

  const removeLanguage = (languageCode: ContentLanguage) => {
    onTranslationsChange(
      translations.filter((draft) => draft.languageCode !== languageCode),
    );
    setOpenLanguage(null);
  };

  const openDraft = translations.find(
    (draft) => draft.languageCode === openLanguage,
  );

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-lg font-semibold text-indigo-800">Languages</h2>

      <div className="mb-4 max-w-md">
        <FormField
          label="Event Language (default)"
          required
          error={languageError}
        >
          {({ id, invalid }) => (
            <Select
              id={id}
              aria-invalid={invalid}
              value={sourceLanguage}
              onChange={(e) =>
                onSourceLanguageChange(e.target.value as ContentLanguage | "")
              }
            >
              <option value="">Select a language</option>
              {CONTENT_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {CONTENT_LANGUAGE_LABELS[language]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <p className="mt-1 text-sm text-gray-600">
          This is the event language, and it is the same language Zoom shows
          attendees.
        </p>
      </div>

      {!sourceLanguage ? null : (
        <>
          <h3 className="mb-2 text-base font-semibold text-indigo-800">
            Public landing page translations
          </h3>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {translations.map((draft) => (
              <button
                key={draft.languageCode}
                type="button"
                onClick={() =>
                  setOpenLanguage(
                    openLanguage === draft.languageCode
                      ? null
                      : draft.languageCode,
                  )
                }
                aria-pressed={openLanguage === draft.languageCode}
                className={`rounded-full border px-3 py-1 text-sm ${
                  openLanguage === draft.languageCode
                    ? "border-indigo-600 bg-indigo-50 text-indigo-800"
                    : "border-gray-300 text-gray-700"
                }`}
              >
                {CONTENT_LANGUAGE_LABELS[draft.languageCode]}
                <span className="ml-1 text-xs text-gray-500">
                  {filledCount(draft) === 0
                    ? "empty"
                    : `${filledCount(draft)} filled`}
                </span>
              </button>
            ))}

            {addable.length > 0 && (
              <Select
                aria-label="Add a language"
                value=""
                onChange={(e) => addLanguage(e.target.value as ContentLanguage)}
                className="w-auto"
              >
                <option value="">
                  {translations.length === 0
                    ? "Add a translation"
                    : "Add another"}
                </option>
                {addable.map((language) => (
                  <option key={language} value={language}>
                    {CONTENT_LANGUAGE_LABELS[language]}
                  </option>
                ))}
              </Select>
            )}
          </div>

          {openDraft && (
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  {CONTENT_LANGUAGE_LABELS[openDraft.languageCode]}
                </h3>
                <button
                  type="button"
                  onClick={() => removeLanguage(openDraft.languageCode)}
                  className="inline-flex items-center gap-1 text-sm text-red-700"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Remove this language
                </button>
              </div>

              <TranslationRow
                label="Title"
                source={title}
                value={openDraft.title}
                onChange={(title) =>
                  updateDraft(openDraft.languageCode, { title })
                }
              />
              <TranslationRow
                label="Description"
                source={description}
                value={openDraft.description}
                multiline
                onChange={(description) =>
                  updateDraft(openDraft.languageCode, { description })
                }
              />

              {sessions.map((session) => (
                <div key={session.key} className="mt-4 border-t pt-4">
                  <p className="mb-2 text-sm font-medium text-gray-800">
                    {session.label}
                  </p>
                  <TranslationRow
                    label="Session name"
                    source={session.sourceName}
                    value={openDraft.sessions[session.key]?.name ?? ""}
                    onChange={(name) =>
                      updateSession(openDraft.languageCode, session.key, {
                        name,
                      })
                    }
                  />
                  <TranslationRow
                    label="Other instructions"
                    source={session.sourceInstructions}
                    value={
                      openDraft.sessions[session.key]?.sessionInstructions ?? ""
                    }
                    multiline
                    onChange={(sessionInstructions) =>
                      updateSession(openDraft.languageCode, session.key, {
                        sessionInstructions,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {translations.length === 0 && addable.length > 0 && (
            <p className="flex items-center gap-1 text-sm text-gray-600">
              <Plus className="h-4 w-4" aria-hidden />
              No translations yet.
            </p>
          )}
        </>
      )}
    </section>
  );
};

interface TranslationRowProps {
  label: string;
  source: string;
  value: string;
  multiline?: boolean;
  onChange: (value: string) => void;
}

/** Source on the left, translation on the right, stacking on narrow screens. */
const TranslationRow = ({
  label,
  source,
  value,
  multiline = false,
  onChange,
}: TranslationRowProps) => (
  <div className="mb-3 grid gap-3 md:grid-cols-2">
    <div>
      <p className="mb-1 text-sm font-medium text-gray-500">{label}</p>
      <p className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
        {source.trim() === "" ? "Not set" : source}
      </p>
    </div>
    <FormField label={label}>
      {({ id }) =>
        multiline ? (
          <textarea
            id={id}
            rows={4}
            className={fieldClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            id={id}
            type="text"
            className={fieldClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    </FormField>
  </div>
);

export default EventTranslations;
